import { defineStore } from "pinia";
import { ref } from "vue";
import MainSearchResult from "~/models/MainSearchResult";
import mainSearch from "~/services/MainSearchService";

const useSearchStore = defineStore("search", () => {
  const searchText = ref<string>();
  const submitSearchText = ref<string>();
  const router = useRouter();
  const route = useRoute();
  const currentPage = computed(
    () => parseInt(route.query?.page as string) || 1,
  );
  const randomSeed = computed(() =>
    route.query.random_seed
      ? parseInt(route.query.random_seed as string)
      : undefined,
  );

  const $setSearchText = (value?: string) => {
    searchText.value = value;
  };

  const $setSubmitSearchText = (value?: string) => {
    submitSearchText.value = value;
  };

  const loading = ref(false);
  const pendingSearch = ref<Promise<MainSearchResult>>();
  const currentResult = ref<MainSearchResult>();
  const hasError = ref(false);
  const lastWasSubmitted = ref(false);

  const rawSearchText = computed(
    () => route.query.search_text as string | undefined,
  );

  const search = async (
    page: number,
    filters: string[],
    searchText?: string,
    randomSeed?: number,
  ) => {
    if (currentResult.value && mainSearch.hasNewFilters(filters)) {
      currentResult.value.data = [];
    }
    const promise = mainSearch.search(
      page,
      filters,
      undefined,
      randomSeed,
      searchText,
    );
    loading.value = true;
    promise
      .then((result) => {
        currentResult.value = result;
      })
      .catch((err) => {
        console.error(err);
        hasError.value = true;
      })
      .finally(() => {
        pendingSearch.value = undefined;
        loading.value = false;
        lastWasSubmitted.value = !!submitSearchText.value;
        submitSearchText.value = undefined;
      });
    pendingSearch.value = promise;
  };

  watch(
    () => submitSearchText.value,
    async () => {
      if (submitSearchText.value) {
        mainSearch.clear();
        pendingSearch.value = undefined;
        currentResult.value = undefined;
        search(1, [], submitSearchText.value, undefined);
        router.push({ name: "search" });
      }
    },
    { immediate: true },
  );

  return {
    searchText,
    submitSearchText,
    $setSearchText,
    $setSubmitSearchText,
    loading,
    pendingSearch,
    currentResult,
    hasError,
    search,
    rawSearchText,
    currentPage,
    randomSeed,
    lastWasSubmitted,
  };
});

export default useSearchStore;
